(() => {
  const mobile = window.matchMedia('(max-width: 900px)');
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const visibleFocusables = (root) => [...root.querySelectorAll(focusableSelector)].filter((element) => {
    if (element.closest('[hidden]') || element.getAttribute('aria-hidden') === 'true') return false;
    return element.getClientRects().length > 0;
  });

  const initializeHeader = (header) => {
    if (!header || header.dataset.bloomMobileFinish === 'true') return;
    const toggle = header.querySelector('[data-nav-toggle]');
    const nav = header.querySelector('[data-nav-links]');
    if (!toggle || !nav) return;
    header.dataset.bloomMobileFinish = 'true';
    const label = toggle.querySelector('span');
    let wasOpen = false;
    let openedWithPointer = false;
    let menuFrame;

    toggle.addEventListener('click', (event) => {
      openedWithPointer = event.detail > 0;
    });

    const syncMenuHeight = () => {
      if (!mobile.matches || !nav.classList.contains('is-open')) return;
      nav.style.setProperty('--bloom-mobile-nav-top', `${Math.max(0, Math.ceil(header.getBoundingClientRect().bottom))}px`);
    };

    const scheduleMenuHeight = () => {
      window.cancelAnimationFrame(menuFrame);
      menuFrame = window.requestAnimationFrame(syncMenuHeight);
    };

    window.addEventListener('resize', scheduleMenuHeight, { passive: true });
    window.addEventListener('scroll', scheduleMenuHeight, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(scheduleMenuHeight).observe(header);

    const syncMega = (mega) => {
      const panel = mega.querySelector('[data-mega-panel]');
      const button = mega.querySelector('[data-mega-toggle]');
      if (!panel) return;
      if (!mobile.matches) {
        panel.inert = false;
        panel.removeAttribute('aria-hidden');
        return;
      }
      const isOpen = mega.classList.contains('is-open');
      panel.inert = !isOpen;
      panel.setAttribute('aria-hidden', String(!isOpen));
      button?.setAttribute('aria-expanded', String(isOpen));
    };

    const syncMegas = () => nav.querySelectorAll('[data-mega]').forEach(syncMega);

    const closeMegas = () => {
      nav.querySelectorAll('[data-mega]').forEach((mega) => mega.classList.remove('is-open'));
      nav.querySelectorAll('[data-mega-toggle]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      syncMegas();
    };

    const close = (restoreFocus = false) => {
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      closeMegas();
      document.body.classList.remove('bloom-mobile-nav-open');
      if (label) label.textContent = 'Menu';
      if (restoreFocus) window.requestAnimationFrame(() => toggle.focus({ preventScroll: true }));
    };

    const sync = () => {
      if (!mobile.matches) {
        nav.inert = false;
        nav.removeAttribute('aria-hidden');
        document.body.classList.remove('bloom-mobile-nav-open');
        if (label) label.textContent = 'Menu';
        syncMegas();
        wasOpen = false;
        return;
      }

      const isOpen = mobile.matches && nav.classList.contains('is-open');
      const focusWasInside = nav.contains(document.activeElement);
      nav.inert = !isOpen;
      nav.setAttribute('aria-hidden', String(!isOpen));
      document.body.classList.toggle('bloom-mobile-nav-open', isOpen);
      if (isOpen) syncMenuHeight();
      if (label) label.textContent = isOpen ? 'Close' : 'Menu';
      if (isOpen && !wasOpen) {
        nav.scrollTop = 0;
        if (!openedWithPointer) {
          window.requestAnimationFrame(() => visibleFocusables(nav)[0]?.focus({ preventScroll: true }));
        }
      }
      if (!isOpen && wasOpen) {
        closeMegas();
        if (focusWasInside) window.requestAnimationFrame(() => toggle.focus({ preventScroll: true }));
      }
      syncMegas();
      wasOpen = isOpen;
    };

    new MutationObserver(sync).observe(nav, { attributes: true, attributeFilter: ['class'] });
    nav.querySelectorAll('[data-mega]').forEach((mega) => {
      new MutationObserver(() => syncMega(mega)).observe(mega, { attributes: true, attributeFilter: ['class'] });
    });

    document.addEventListener('pointerdown', (event) => {
      if (mobile.matches && nav.classList.contains('is-open') && !header.contains(event.target)) close(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && mobile.matches && nav.classList.contains('is-open')) {
        event.preventDefault();
        close(true);
      }
    });

    header.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !mobile.matches || !nav.classList.contains('is-open')) return;
      const focusables = visibleFocusables(header);
      if (!focusables.length) {
        event.preventDefault();
        toggle.focus({ preventScroll: true });
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    nav.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) close(false);
    });

    mobile.addEventListener?.('change', () => {
      if (!mobile.matches) close(false);
      sync();
    });

    sync();
  };

  const initializeFilters = (catalog) => {
    if (!catalog || catalog.dataset.bloomMobileFinish === 'true') return;
    const panel = catalog.querySelector('[data-filter-panel]');
    const opener = catalog.querySelector('[data-filter-open]');
    const closeButton = catalog.querySelector('[data-filter-close]');
    if (!panel || !opener) return;
    catalog.dataset.bloomMobileFinish = 'true';
    let wasOpen = false;

    const close = (restoreFocus = false) => {
      panel.classList.remove('is-open');
      opener.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('filters-open');
      if (restoreFocus) window.requestAnimationFrame(() => opener.focus({ preventScroll: true }));
    };

    const sync = () => {
      if (!mobile.matches) {
        panel.inert = false;
        panel.removeAttribute('aria-hidden');
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
        document.body.classList.remove('filters-open');
        wasOpen = false;
        return;
      }

      const isOpen = panel.classList.contains('is-open');
      const focusWasInside = panel.contains(document.activeElement);
      panel.inert = !isOpen;
      panel.setAttribute('aria-hidden', String(!isOpen));
      if (isOpen) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        if (!panel.hasAttribute('aria-label') && !panel.hasAttribute('aria-labelledby')) panel.setAttribute('aria-label', 'Product filters');
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
      }
      document.body.classList.toggle('filters-open', isOpen);
      opener.setAttribute('aria-expanded', String(isOpen));

      if (isOpen && !wasOpen) {
        window.requestAnimationFrame(() => (closeButton || visibleFocusables(panel)[0])?.focus({ preventScroll: true }));
      } else if (!isOpen && wasOpen && focusWasInside) {
        window.requestAnimationFrame(() => opener.focus({ preventScroll: true }));
      }
      wasOpen = isOpen;
    };

    new MutationObserver(sync).observe(panel, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('click', (event) => {
      if (!mobile.matches || !panel.classList.contains('is-open') || panel.contains(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      close(true);
    }, true);

    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !mobile.matches || !panel.classList.contains('is-open')) return;
      const focusables = visibleFocusables(panel);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && mobile.matches && panel.classList.contains('is-open')) {
        event.preventDefault();
        close(true);
      }
    });

    mobile.addEventListener?.('change', () => {
      if (!mobile.matches) close(false);
      sync();
    });

    sync();
  };

  const initializeFooter = (footer) => {
    if (!footer || footer.dataset.bloomCompactFooter === 'true') return;
    const accordions = [...footer.querySelectorAll('.footer-accordion')];
    if (!accordions.length) return;
    footer.dataset.bloomCompactFooter = 'true';
    const compact = window.matchMedia('(max-width: 760px)');
    const sync = () => {
      accordions.forEach((details) => {
        const summary = details.querySelector('summary');
        if (compact.matches && details.contains(document.activeElement)) summary?.focus({ preventScroll: true });
        details.open = !compact.matches;
        if (summary) summary.tabIndex = compact.matches ? 0 : -1;
      });
    };
    accordions.forEach((details) => {
      details.querySelector('summary')?.addEventListener('click', (event) => {
        if (!compact.matches) event.preventDefault();
      });
    });
    compact.addEventListener?.('change', sync);
    sync();
  };

  const initializeRetaMobileTrial = (page) => {
    if (!page || page.dataset.productHandle !== 'retatrutide' || page.dataset.retaMobileTrial === 'true') return;
    const info = page.querySelector('.v3-product-info');
    const title = info?.querySelector('h1');
    const price = info?.querySelector('[data-product-price]');
    const gallery = page.querySelector('.v3-product-gallery');
    const story = info?.querySelector('.v3-product-story');
    const purchase = info?.querySelector('.v3-product-form');
    const introduction = story?.querySelector('.v3-product-story__intro')?.textContent.trim();
    if (!info || !title || !price || !gallery || !story || !purchase || !introduction) return;

    page.dataset.retaMobileTrial = 'true';
    const phone = window.matchMedia('(max-width: 749px)');
    const summary = document.createElement('p');
    summary.className = 'v3-reta-mobile-summary';
    summary.textContent = introduction.match(/^[\s\S]*?[.!?](?:\s|$)/)?.[0].trim() || introduction;
    let positions = null;

    const sync = () => {
      if (!page.isConnected) {
        if (phone.removeEventListener) phone.removeEventListener('change', sync);
        else phone.removeListener(sync);
        return;
      }
      if (phone.matches && !positions) {
        positions = [gallery, price, story].map((element) => {
          const marker = document.createTextNode('');
          element.parentNode.insertBefore(marker, element);
          return { element, marker };
        });
        title.after(price, gallery, summary);
        purchase.after(story);
        page.dataset.retaMobileActive = 'true';
      } else if (!phone.matches && positions) {
        summary.remove();
        positions.forEach(({ element, marker }) => {
          marker.parentNode.insertBefore(element, marker);
          marker.remove();
        });
        positions = null;
        delete page.dataset.retaMobileActive;
      }
    };

    if (phone.addEventListener) phone.addEventListener('change', sync);
    else phone.addListener(sync);
    sync();
  };

  const initialize = (root = document) => {
    root.querySelectorAll?.('.site-header').forEach(initializeHeader);
    root.querySelectorAll?.('[data-bloom-catalog]').forEach(initializeFilters);
    root.querySelectorAll?.('#SiteFooter').forEach(initializeFooter);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
